// Install the global /api/ fetch gate before the app renders, so no component
// can fire an unauthenticated request during first paint. See lib/api-fetch.ts.
//
// NB: ES imports are hoisted, so this call does NOT run before the imports
// below are evaluated — it runs after all of them, and before createRoot().
// That is sufficient today because no module fetches /api/ at import time. If
// one ever does, it will bypass this gate; move the install into its own
// module imported first rather than relying on statement order here.
import { installApiFetch } from './lib/api-fetch'
installApiFetch()

import './i18n'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'

import { AuthProvider } from './lib/auth'
import { ProtectedRoute } from './components/ProtectedRoute'
import { PermGate } from './components/FinancialGate'

import { App } from './App'
import { LoginView } from './views/Login'

// CFO Console views
import { PulseView }       from './views/Pulse'
import { IncomeView }      from './views/Income'
import { CashView }        from './views/Cash'
import { ShipmentsView }   from './views/Shipments'
import { CustomersView }   from './views/Customers'
import { PricingView }     from './views/Pricing'
import { AskView }         from './views/Ask'
import { MyDashboardView } from './views/MyDashboard'
import { GlossaryView }    from './views/Glossary'

// Existing views
import { HomeView }        from './views/Home'
import { UploadsView }     from './views/Uploads'
import { CarrierAdjustmentsView } from './views/CarrierAdjustments'
import { DebugFilesView }  from './views/DebugFiles'
import { AuditLogView }    from './views/AuditLog'
import { CarriersView }    from './views/Carriers'
import { ExportsView }     from './views/Exports'
import { InvoicesView }    from './views/Invoices'
import { AffiliatesView }  from './views/Affiliates'
import { CarrierRecoveryView } from './views/CarrierRecovery'
import { CarrierInvoicesView } from './views/CarrierInvoices'
import { SettingsView }        from './views/Settings'
import { DiagnosticsView }    from './views/Diagnostics'
import { StripeView }         from './views/Stripe'
import { OrphansView }       from './views/Orphans'
import { LookupView }        from './views/Lookup'

// Permission-gated views
import { PersonnelView }       from './views/Personnel'
import { PersonnelDetailView } from './views/PersonnelDetail'
import { CostsView }           from './views/Costs'
import { UsersView }           from './views/Users'

const qc = new QueryClient()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginView />} />

            {/* All routes require authentication */}
            <Route element={<ProtectedRoute />}>
              <Route element={<App />}>
                <Route index element={<Navigate to="/dashboard" replace />} />

                {/* Always accessible to authenticated users */}
                <Route path="/dashboard" element={<MyDashboardView />} />
                <Route path="/glossary"  element={<GlossaryView />} />
                <Route path="/home"      element={<HomeView />} />
                <Route path="/carriers"      element={<CarriersView />} />
                <Route path="/carriers/:code" element={<CarriersView />} />

                {/* Permission-gated — PermGate shows a locked placeholder if denied */}
                <Route path="/pulse"       element={<PermGate perm="pulse">      <PulseView />       </PermGate>} />
                <Route path="/income"      element={<PermGate perm="pulse">      <IncomeView />      </PermGate>} />
                <Route path="/ask"         element={<PermGate perm="ask">        <AskView />         </PermGate>} />
                <Route path="/cash"        element={<PermGate perm="cash">       <CashView />        </PermGate>} />
                <Route path="/shipments"   element={<PermGate perm="ships">      <ShipmentsView />   </PermGate>} />
                <Route path="/customers"   element={<PermGate perm="customers">  <CustomersView />   </PermGate>} />
                <Route path="/pricing"     element={<PermGate perm="pricing">    <PricingView />     </PermGate>} />
                <Route path="/affiliates"  element={<PermGate perm="affiliates"> <AffiliatesView />  </PermGate>} />
                <Route path="/invoices"    element={<PermGate perm="invoices">   <InvoicesView />    </PermGate>} />
                <Route path="/carrier-adjustments" element={<PermGate perm="recon"><CarrierAdjustmentsView /></PermGate>} />
                {/* Disputes + Claims are one page; the old links open the matching tab */}
                <Route path="/recovery"    element={<CarrierRecoveryView />} />
                <Route path="/disputes"    element={<Navigate to="/recovery?tab=disputes" replace />} />
                <Route path="/claims"      element={<Navigate to="/recovery?tab=claims" replace />} />
                <Route path="/carrier-invoices" element={<PermGate perm="invoices"> <CarrierInvoicesView /> </PermGate>} />
                <Route path="/personnel"   element={<PermGate perm="personnel">  <PersonnelView />   </PermGate>} />
                <Route path="/personnel/:id" element={<PermGate perm="personnel"><PersonnelDetailView /></PermGate>} />
                <Route path="/costs"       element={<PermGate perm="costs">      <CostsView />       </PermGate>} />
                <Route path="/users"       element={<PermGate perm="users">      <UsersView />       </PermGate>} />
                <Route path="/uploads"     element={<PermGate perm="uploads">    <UploadsView />     </PermGate>} />
                <Route path="/exports"     element={<PermGate perm="exports">    <ExportsView />     </PermGate>} />
                <Route path="/settings"     element={<PermGate perm="settings">   <SettingsView />    </PermGate>} />
                <Route path="/diagnostics" element={<DiagnosticsView />} />
                {/* owner/admin only — the view and the API both enforce it */}
                <Route path="/debug-files" element={<DebugFilesView />} />
                <Route path="/activity-log" element={<AuditLogView />} />
                <Route path="/stripe"      element={<PermGate perm="cash"><StripeView /></PermGate>} />
                <Route path="/orphans"     element={<PermGate perm="recon"><OrphansView /></PermGate>} />
                <Route path="/lookup"         element={<LookupView />} />
                <Route path="/lookup/:tracking" element={<LookupView />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
)
